// ============================================================================
// pendingSend.js — egy félbeszakadt küldés megőrzése újratöltésen át.
//
// A PROBLÉMA: a küldési azonosító (idempotencia) eddig csak a memóriában
// élt. Ha a küldés közben az oldal újratöltődött, vagy a telefon a háttérbe
// tett alkalmazást leállította (iOS-en gyakori), a piszkozat ÉS az azonosító
// is elveszett. A felhasználó nem tudta, elment-e az üzenet; ha újraírta,
// dupla üzenet lehetett belőle, ha nem, elveszett.
//
// A MEGOLDÁS (docs/archive/SPECIFICATION.md 7. vége, a getSendStatus
// végpont eredeti célja):
//   1. Küldés előtt feljegyezzük: ki, melyik szobába, mit, milyen
//      azonosítókkal küld, és hány darab ment már el.
//   2. Induláskor, ha van ilyen feljegyzés, a szervertől megkérdezzük
//      (getSendStatus), melyik darab érkezett meg.
//   3. Ami NEM érkezett meg, visszakerül a beviteli mezőbe. Ha egyetlen darab
//      sem ment el, UGYANAZZAL az azonosítóval - így ha a szerver mégis
//      megkapta volna, sem lesz belőle második példány.
//
// MIÉRT localStorage és nem sessionStorage (ahogy a specifikáció írta):
// iOS-en a háttérbe tett, telepített alkalmazást a rendszer leállítja, és
// az újraindítás ÚJ munkamenet - a sessionStorage ilyenkor üres. Épp ez a
// leggyakoribb eset, amikor egy küldés félbemarad. Cserébe a feljegyzés a
// felhasználóhoz kötött (uid), 24 óra után érvénytelen, és kijelentkezéskor
// törlődik - egy közös eszközön más nem kapja vissza a szövegét.
//
// Csak SZÖVEGES üzenetre vonatkozik: a kép (blob) nem menthető el
// ésszerűen, és egy kép nélküli újraküldés más tartalom lenne.
//
// Ez a modul tiszta logika (Firebase és DOM nélkül), a tárolót és a
// szerverhívást paraméterként kapja - így Node-ban is tesztelhető.
// ============================================================================

export const FUGGO_KULCS = "lineage0_fuggo_kuldes";
export const FUGGO_ELETTARTAM_MS = 24 * 60 * 60 * 1000;

function tarolo(storage) {
  if (storage) return storage;
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch (e) {
    return null;
  }
}

/**
 * Egy küldési szándék feljegyzése.
 * @param {{uid:string, roomId:string, draft:string, darabok:string[], darabIds:string[], kesz?:number}} rec
 */
export function mentFuggo(rec, storage, now = Date.now()) {
  const s = tarolo(storage);
  if (!s) return;
  try {
    s.setItem(FUGGO_KULCS, JSON.stringify({ ...rec, kesz: rec.kesz || 0, ts: now }));
  } catch (e) {
    // Megtelt vagy tiltott tároló: a helyreállítás kimarad, a küldés nem.
  }
}

/** Az elküldött darabok számának frissítése a feljegyzésben. */
export function frissitKesz(kesz, storage) {
  const s = tarolo(storage);
  if (!s) return;
  try {
    const rec = JSON.parse(s.getItem(FUGGO_KULCS) || "null");
    if (rec) s.setItem(FUGGO_KULCS, JSON.stringify({ ...rec, kesz }));
  } catch (e) {
    // nem kritikus
  }
}

export function torolFuggo(storage) {
  const s = tarolo(storage);
  if (!s) return;
  try {
    s.removeItem(FUGGO_KULCS);
  } catch (e) {
    // nem kritikus
  }
}

/**
 * A feljegyzés törlése, de CSAK ha a megadott felhasználóé és szobáé.
 * Gépeléskor ezt hívjuk: egy MÁSIK szoba kétes küldése attól még kétes,
 * hogy itt valaki gépel - annak a feljegyzése maradjon.
 */
export function torolFuggoSzobaban(uid, roomId, storage) {
  const s = tarolo(storage);
  if (!s) return;
  try {
    const rec = JSON.parse(s.getItem(FUGGO_KULCS) || "null");
    if (rec && rec.uid === uid && rec.roomId === roomId) s.removeItem(FUGGO_KULCS);
  } catch (e) {
    // nem kritikus
  }
}

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * A feljegyzés beolvasása és ellenőrzése. Érvénytelen, lejárt vagy más
 * felhasználóé: törlődik, és null a válasz.
 */
export function olvasFuggo(uid, storage, now = Date.now()) {
  const s = tarolo(storage);
  if (!s) return null;
  let rec;
  try {
    rec = JSON.parse(s.getItem(FUGGO_KULCS) || "null");
  } catch (e) {
    rec = null;
  }
  if (!rec) {
    torolFuggo(s);
    return null;
  }
  const ervenyes =
    typeof rec.uid === "string" && rec.uid === uid &&
    typeof rec.roomId === "string" && rec.roomId &&
    typeof rec.draft === "string" &&
    Array.isArray(rec.darabok) && Array.isArray(rec.darabIds) &&
    rec.darabok.length > 0 && rec.darabok.length === rec.darabIds.length &&
    rec.darabIds.every((id) => typeof id === "string" && UUID_V4.test(id)) &&
    Number.isInteger(rec.kesz) && rec.kesz >= 0 && rec.kesz <= rec.darabok.length &&
    typeof rec.ts === "number" && now - rec.ts < FUGGO_ELETTARTAM_MS && now - rec.ts >= 0;
  if (!ervenyes) {
    // Más felhasználó feljegyzését is töröljük: a kijelentkezés elvileg
    // már megtette, de egy megszakadt kijelentkezés után se maradjon ott.
    torolFuggo(s);
    return null;
  }
  return rec;
}

/**
 * Mi a teendő a feljegyzéssel? A szervert darabonként kérdezi, sorrendben.
 *
 * @param {object} rec olvasFuggo() eredménye
 * @param {(q:{roomId:string, clientMessageId:string}) => Promise<{found:boolean}>} getStatus
 * @returns {Promise<
 *   {teendo:"kesz"} |                       // minden darab megérkezett
 *   {teendo:"elvet"} |                      // a szoba már nem elérhető
 *   {teendo:"visszaallit", draft:string, pendingSendId:string|null, darabIds:string[]|null}
 * >}
 * Hálózati hibánál KIVÉTELT dob: ilyenkor a feljegyzés marad, és a
 * következő indításkor újra próbáljuk.
 */
export async function helyreallitasTerv(rec, getStatus) {
  let elsoHianyzo = -1;
  for (let i = rec.kesz; i < rec.darabIds.length; i++) {
    let valasz;
    try {
      valasz = await getStatus({ roomId: rec.roomId, clientMessageId: rec.darabIds[i] });
    } catch (err) {
      const code = String((err && err.code) || "");
      // A szoba megszűnt, archiválták, vagy már nincs hozzáférés: ide
      // úgysem lehetne újraküldeni.
      if (
        code === "functions/not-found" ||
        code === "functions/permission-denied" ||
        code === "functions/failed-precondition"
      ) {
        return { teendo: "elvet" };
      }
      throw err;
    }
    if (!valasz || valasz.found !== true) {
      elsoHianyzo = i;
      break;
    }
  }

  if (elsoHianyzo === -1) return { teendo: "kesz" };

  if (elsoHianyzo === 0) {
    // Semmi nem ment el: az EREDETI piszkozat (a "#" jelöléssel együtt)
    // és az EREDETI azonosítók jönnek vissza.
    return { teendo: "visszaallit", draft: rec.draft, pendingSendId: rec.darabIds[0], darabIds: rec.darabIds };
  }

  // Néhány darab már elment: csak a maradék jön vissza, ÚJ küldési
  // szándékként - ugyanúgy, mint a részleges küldési hibánál (main.js).
  return {
    teendo: "visszaallit",
    draft: rec.darabok.slice(elsoHianyzo).join("\n\n"),
    pendingSendId: null,
    darabIds: null,
  };
}

/** Van-e (bármelyik felhasználótól) félbemaradt küldés feljegyezve ezen az eszközön? (közös eszközre váltás előtti figyelmeztetéshez) */
export function vanFuggo(storage) {
  const t = tarolo(storage);
  try {
    return Boolean(t && t.getItem(FUGGO_KULCS));
  } catch (e) {
    return false;
  }
}
